import { z } from 'zod';
import { googleKeywordTextSchema } from './google-keyword.js';
import { googlePaidStrategyOutputSchema } from './google-paid-strategy.js';
import { campaignAnomalyHintSchema } from './mag-followup.js';
import { metaContentPlanOutputSchema } from './meta-content-plan.js';
import { metaPaidStrategyOutputSchema } from './meta-paid-strategy.js';
import { paidScheduleOutputSchema } from './paid-schedule.js';
// Campaign draft schemas — new-campaign handoff §4.4. Mag turns a one-
// paragraph brief into a structured proposal: audience, budget, creative
// variants, what we're optimizing for, when it goes live, and one
// decision she can't make alone. Lives in @magig/schemas so the agent's
// output, the API response, and the dashboard renderer share one shape.
export const campaignPlatformSchema = z.enum(['meta', 'google', 'tiktok']);
/** Audience description Mag drafts from the brand brain's customer section.
 *  No real demographic API plugged in yet — the rationale string is Mag's
 *  own explanation of how she landed here. */
export const campaignAudienceSchema = z.object({
    /** One-line description in Mag's voice ("founders 28-40 in b2b saas
     *  signals, english speaking"). */
    description: z.string().min(1).max(280),
    /** 4-7 compact chip tags ("age 28-40", "b2b saas", "founders"). */
    tags: z.array(z.string().min(1).max(40)).min(3).max(8),
    /** Rough reachable count Mag is targeting. Free-form string ("~840k",
     *  "~120k") since real platform reach lookups aren't wired yet. */
    reachable: z.string().min(1).max(40),
});
export const campaignPlatformSplitSchema = z.object({
    platform: campaignPlatformSchema,
    /** Whole-dollar amount per day for this platform. */
    dailyUsd: z.number().int().min(0).max(100_000),
    /** Mag's one-line reason — "warmer traffic", "cheaper clicks", etc.
     *  Cap raised 160→280 so spanish/portuguese splits don't get clipped. */
    rationale: z.string().min(1).max(280),
});
export const campaignBudgetSchema = z.object({
    /** Total daily cap across all platforms. */
    dailyUsd: z.number().int().min(1).max(100_000),
    /** Which week of the run this number applies to — Mag usually opens
     *  conservatively then scales. */
    phase: z.string().min(1).max(40), // "week one", "first 3 days"…
    /** Founder-facing explanation: why this number, why this phase.
     *  Cap raised 400→800 so the multi-week rollout logic fits without
     *  mid-sentence truncation. The prompt surfaces this cap to the LLM
     *  so it writes within range. */
    rationale: z.string().min(1).max(800),
    /** Per-platform split. Sum should equal dailyUsd ± rounding. */
    split: z.array(campaignPlatformSplitSchema).min(1).max(3),
});
/** One creative variant Mag drafts. Mag returns 6 of these. Just a hook +
 *  background hint today — image gen happens through the existing
 *  /api/feed/variants/:id/image path once ad creative shipping lands. */
export const campaignCreativeSchema = z.object({
    /** Short tagline copy. Lowercase, Instrument Serif italic in the UI. */
    hook: z.string().min(1).max(120),
    /** UI tile background — Mag picks per variant so the batch reads as
     *  intentional, not random. */
    bg: z.enum(['spell', 'spell_soft', 'cream', 'ink']),
});
/** The one decision Mag actually needs the founder to make. The whole
 *  REVIEW screen converges to this. */
export const campaignOneAskSchema = z.object({
    /** Eyebrow Mag uses to label it ("ONE THING I NEED YOU TO DECIDE"). */
    eyebrow: z.string().min(1).max(80).default('ONE THING I NEED YOU TO DECIDE'),
    /** The question, in Mag voice. */
    question: z.string().min(1).max(280),
    /** The two answer buttons — first is the recommended path. */
    options: z
        .array(z.object({
        label: z.string().min(1).max(60),
        choice: z.string().min(1).max(40), // sent back on launch
    }))
        .length(2),
});
/** Full Mag-drafted campaign proposal. The REVIEW screen renders this verbatim. */
export const campaignDraftSchema = z.object({
    /** Mag's name for the campaign, lowercase ("tuesday lookalike"). */
    name: z.string().min(1).max(80),
    /** 2-3 sentence prose pitch in Mag's voice — opens the REVIEW screen. */
    pitch: z.string().min(1).max(1000),
    audience: campaignAudienceSchema,
    budget: campaignBudgetSchema,
    /** Exactly six creative tiles to render in the proposal grid. */
    creative: z.array(campaignCreativeSchema).length(6),
    /** Single-word answer rendered on the "optimizing for" panel ("paying
     *  signups."). */
    optimizingFor: z.string().min(1).max(80),
    /** Single line: when it goes live + first read date ("live tomorrow 9am.
     *  first read by friday."). */
    timeline: z.string().min(1).max(160),
    oneAsk: campaignOneAskSchema,
});
/** Progress checklist row — the DRAFTING screen reads this array straight
 *  from the server (handoff §8.3, "drafting checklist is the source of
 *  truth"). Server flips `state` from pending → active → done. */
export const campaignProgressStepSchema = z.object({
    /** Stable id so the client can keyframe per-row animations. */
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(120),
    state: z.enum(['pending', 'active', 'done']),
});
export const campaignProgressSchema = z.object({
    steps: z.array(campaignProgressStepSchema).min(1).max(10),
    /** Honest ETA in seconds — the DRAFTING screen surfaces it ("~45
     *  SECONDS"). Falls to 0 when done. */
    etaSeconds: z.number().int().min(0).max(600),
});
/** The statuses that reach the WIRE. Narrower than the db enum on purpose —
 *  'proposed' and 'closed' are internal lifecycle states the dashboard has
 *  never rendered — but it had drifted BELOW what the api can actually return:
 *
 *  - 'paused' is written by campaign-level pause (spec da9f7023). It had been
 *    in the db enum since it was defined, unwritten, so this schema never
 *    needed it; the moment something wrote it, every consumer typed off this
 *    enum would have been reasoning about a status it believed impossible.
 *  - 'archived' is written by delete (decision 261f69c), which archives rather
 *    than hard-deleting. listCampaigns filters archived rows out, but the
 *    DETAIL route still serves one by id, so it reaches the wire.
 *
 *  Both were reachable before being added here, which is the shape to watch:
 *  this enum is a claim about the api's output, and the api stopped honouring
 *  it without anything failing. */
export const campaignStatusSchema = z.enum([
    'intake',
    'drafting',
    'ready',
    'launched',
    'failed',
    'paused',
    'archived',
]);
// Mag's scan state on a campaign (s126 → s170).
//   - 'off'    — paused by the founder; scheduler ignores
//   - 'manual' — no automatic scans; founder-triggered only
//   - 'auto'   — scheduler controls cadence via campaigns.next_scan_at
//                (which is set per-scan by the LLM heartbeat, s160)
//   - 'daily' / 'weekly' — legacy values from the per-campaign-repeatable
//                era. Treat as 'auto'; new code shouldn't write them.
// Wire shape mirrors the pg enum in packages/db; daily/weekly stay in the
// union because we can't drop enum values cleanly in Postgres.
export const campaignScanCadenceSchema = z.enum(['off', 'manual', 'daily', 'weekly', 'auto']);
/** True when the campaign is on a scheduler-controlled cadence —
 *  i.e. mag is reading it on her own. Treats the legacy daily/weekly
 *  values as auto so older rows behave correctly until they're
 *  re-scanned (which migrates them to 'auto'). */
export function isAutoScanning(cadence) {
    return cadence === 'auto' || cadence === 'daily' || cadence === 'weekly';
}
// ─── Multi-turn intake (s120) ───────────────────────────────────────────
// Mag asks clarifying questions, classifies the campaign kind, then kicks
// the right downstream pipeline. The schemas below live here so the api,
// dashboard, and intake agent all agree on the wire shape.
/** One turn in the intake chat. Mag's first turn is seeded server-side
 *  from the founder's initial brief — the chat screen renders the array
 *  in order.
 *
 *  Plan-mode (post-classify) adds 'panel-progress' turns that surface
 *  agent/tool call lines as the multi-agent panel runs (drafter →
 *  critics → synthesizer). They render inline in the chat as small
 *  monospace log lines distinct from the conversational mag/founder
 *  bubbles. */
export const intakeMessageSchema = z.object({
    role: z.enum(['mag', 'founder', 'panel-progress']),
    body: z.string().min(1).max(4000),
    createdAt: z.string().datetime(),
});
/** What kind of campaign Mag classified this as. Determines which
 *  downstream draft agent runs once intake ends. */
export const campaignKindSchema = z.enum([
    'unclassified',
    'creative_ads',
    'conversion_path',
    'analytics_setup',
    // Added to keep this Zod enum in sync with the campaigns.kind
    // pgEnum (packages/db/src/schema/campaigns.ts), which gained
    // 'organic_meta' as part of the meta-cm phase 3 content-plan
    // work. Without it, campaigns.ts + feed.ts fail to typecheck
    // when reading the DB row's kind field.
    'organic_meta',
    // Unification roadmap Phase 1 — dedicated kind for takeover
    // imports (google ads / meta ads) so briefed-only filters
    // (kind='creative_ads' AND status='launched' across several
    // schedulers) don't accidentally start pulling imported rows.
    // The provider column on the campaigns row distinguishes
    // google vs meta within this kind.
    'imported_paid',
]);
/** [spec 45ea7825] An angle the founder just turned down, captured on the
 *  turn they said so.
 *
 *  ON BOTH ARMS of the result union, because a rejection almost always lands
 *  on an 'ask' turn — the founder says no, mag re-recommends, the
 *  conversation continues — and only sometimes on the turn she classifies.
 *  Putting it on 'classify' alone would miss the common case entirely.
 *
 *  CAPTURED AT THE ENVELOPE rather than by a second pass over the transcript:
 *  the agent is the only thing in the loop that has already understood the
 *  rejection, because it rewrote its whole recommendation around it. A
 *  separate classifier re-derives that judgement from the same text and can
 *  disagree with the model that was actually in the conversation. */
export const rejectedAngleSchema = z.object({
    /** The angle in the words it was proposed and refused in. Verbatim: this
     *  is quoted back to the founder later, and a tidied paraphrase is not the
     *  thing they turned down. */
    line: z.string().min(1).max(400),
    /** WHY — and it is required, not decoration. "too technical for our buyer"
     *  is permanent; "we tried it in July and the CPA was bad" is a measurement
     *  that can change; "legal won't let us say that" is absolute. Mag is
     *  allowed to raise a rejected angle again with grounds, and she cannot
     *  weigh that without knowing which of these it was. */
    reason: z.string().min(1).max(600),
});
/** [spec ef8b56a6 S1] The budget the founder declared for this campaign, in
 *  the unit they said it in.
 *
 *  THIS IS THE ONE FOUNDER-OWNED SIGNAL THAT HAD NO FIELD. The intake prompt
 *  gates classification on three of them — budget, goal and angle — and
 *  campaignKind, briefSummary and targetCloseDate all have somewhere to land.
 *  The budget survived only as prose inside briefSummary, and the draft agent
 *  then re-derived a daily integer from that prose, which is where the
 *  founder's monthly figure was lost. Measured 2026-10-13:
 *  brands.monthly_ad_budget_cents was NULL on all six brands and
 *  campaigns.paid_budget_cents on all fifteen campaigns, so no declaration
 *  existed anywhere and every spend ceiling had to be derived from past spend
 *  — a ceiling that structurally cannot authorise growth (task 158075b).
 *
 *  `period` IS REQUIRED AND NEVER DEFAULTED. A missing period read as either
 *  value is a 30x error in what mag may spend, in either direction, and
 *  nothing downstream can catch it because both readings are plausible —
 *  $300/month and $300/day are each a sentence a founder might say. It also
 *  preserves which number the founder ACTUALLY said: "$10/day" quoted back to
 *  someone who said "$300 a month" is a small infidelity that costs trust in
 *  the whole sentence, even though the two resolve to the same envelope.
 *
 *  `confirmed` CARRIES THE PROMPT'S OWN TEST, not a new one: the founder
 *  named the number, or explicitly accepted mag's proposal ("yes", "go with
 *  that", "$25 is fine"). It exists because the prompt contradicts itself
 *  about consent — it states "SILENCE IS NOT CONSENT — for BUDGET, GOAL and
 *  ANGLE" and then defaults all three to mag's own proposals at the
 *  three-turn hard ceiling, so a number mag invented can arrive downstream
 *  wearing the founder's authority. Founder, 2026-10-13: "we need explicit
 *  confirmation from the founder on the budget." Unconfirmed is still
 *  recorded — it is what mag is drafting against — but only a confirmed
 *  declaration becomes a spending ceiling (decision 8ede370).
 *
 *  OPTIONAL, because analytics_setup and conversion_path classifies have no
 *  budget to declare and inventing one would be worse than its absence. */
export const declaredBudgetSchema = z.object({
    /** Whole dollars, in the period below. The cap mirrors
     *  campaignBudgetSchema.dailyUsd's ceiling rather than the deliverable
     *  schemas' 10,000 — a monthly figure is legitimately ~30x a daily one. */
    amountUsd: z.number().int().min(1).max(100_000),
    /** The unit the founder spoke in. Required; see the note above. */
    period: z.enum(['month', 'day']),
    /** True only when the founder named this number or explicitly accepted
     *  mag's. False for anything mag defaulted on their behalf. */
    confirmed: z.boolean(),
});
/** Monthly cents for a declaration, or null when there is nothing a ceiling
 *  may be built from.
 *
 *  THE `* 30 * 100` IS LIFTED VERBATIM from acceptStrategyProposal, which has
 *  written campaigns.paid_budget_cents this way since the strategist shipped.
 *  Two writers of one column that disagreed about whether it held a daily or
 *  a monthly figure would be a 30x error nobody could see, so the convention
 *  is copied rather than re-derived.
 *
 *  Returns null for an UNCONFIRMED declaration: that is the whole point of
 *  the flag, and putting the check here rather than at the call site means a
 *  second writer cannot forget it. */
export function declaredMonthlyBudgetCents(declaration) {
    if (!declaration?.confirmed)
        return null;
    return declaration.period === 'month'
        ? declaration.amountUsd * 100
        : declaration.amountUsd * 30 * 100;
}
/** Result of one intake-agent turn. Either Mag asks another question
 *  ('ask') or she's ready to classify and the downstream pipeline can
 *  start ('classify'). The discriminator lets the api dispatch without
 *  parsing prose. */
export const intakeAgentResultSchema = z.discriminatedUnion('decision', [
    z.object({
        decision: z.literal('ask'),
        /** Mag's next message to the founder. */
        reply: z.string().min(1).max(4000),
        /** Set only on a turn where the founder rejected the angle she had
         *  proposed. Optional everywhere: most turns carry no rejection. */
        rejectedAngle: rejectedAngleSchema.optional(),
    }),
    z.object({
        decision: z.literal('classify'),
        /** Mag's final closing message before drafting kicks off. */
        reply: z.string().min(1).max(4000),
        /** As on the 'ask' arm — a founder can reject an angle in the same
         *  breath as approving everything else. */
        rejectedAngle: rejectedAngleSchema.optional(),
        /** What kind of campaign this is. Drives the downstream agent. */
        campaignKind: campaignKindSchema.exclude(['unclassified']),
        /** Mag's distilled brief — what she heard, in her words. Saved onto
         *  the campaign and used as input to the downstream draft agent. */
        briefSummary: z.string().min(1).max(2000),
        /** [spec 06106f46] The close date Mag RECOMMENDS for this campaign —
         *  when it stops and she writes its closing read. Duration is
         *  mag-owned (project rule 8373ac22): the founder's stated window is
         *  a hypothesis she assesses, not an instruction she obeys.
         *
         *  Validated here for SHAPE ONLY — a real calendar date in
         *  YYYY-MM-DD. The semantic bounds (no earlier than the brand's
         *  tier floor, no later than +90d) are enforced by the api, not
         *  here, because both are relative to today IN THE BRAND'S
         *  TIMEZONE and this schema has no reference date. Comparing
         *  against a UTC `new Date()` would be wrong by a day in either
         *  direction depending on the brand's offset — rejecting a valid
         *  brand-today date for a brand ahead of UTC, or admitting an
         *  already-past one for a brand behind it. The api computes the
         *  floor from DEFAULT_WINDOW_BY_TIER, hands it to the agent as a
         *  hard constraint, and clamps to it on the way in.
         *
         *  Optional because not every classified campaign gets one: only
         *  `creative_ads` is stamped (the closer's verdict is meaningless
         *  for an analytics-instrumentation task), and an omission on a
         *  creative_ads classify falls back to the tier floor rather than
         *  leaving the campaign open forever. */
        targetCloseDate: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/, 'targetCloseDate must be YYYY-MM-DD')
            .refine(isRealCalendarDate, 'targetCloseDate must be a real calendar date')
            .optional(),
        /** [spec ef8b56a6 S1] What the founder said their budget is, and whether
         *  they said it. See declaredBudgetSchema for why the period and the
         *  confirmation are both load-bearing. */
        declaredBudget: declaredBudgetSchema.optional(),
    }),
]);
/** True when a YYYY-MM-DD string names a date that actually exists.
 *  The regex alone admits 2026-02-30 and 2026-13-01; Date parsing
 *  silently rolls those forward, so a round-trip comparison is what
 *  catches them. */
function isRealCalendarDate(value) {
    const parsed = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime()))
        return false;
    return parsed.toISOString().slice(0, 10) === value;
}
// ─── analytics_setup draft (s121) ───────────────────────────────────────
// When Mag classifies a campaign as analytics_setup, she drafts a list of
// events the founder should track + the snippets to fire them. Distinct
// from creative-ads CampaignDraft — different review surface, different
// "launch" semantics (copy the snippets, not push to Meta).
/** One event Mag wants the founder to instrument. Each event carries its
 *  own justification, install location, and a paste-ready snippet. */
export const analyticsSetupEventSchema = z.object({
    /** Stable id so the review UI can keyframe per-event animations and
     *  the copy-buttons key off something predictable. */
    id: z.string().min(1).max(64),
    /** Event name as it should be fired ('purchase_completed',
     *  'wizard_step_completed'). Lowercase, snake_case — Mag picks. */
    name: z.string().min(1).max(80),
    /** Mag's one-line justification: "this is the actual buy event, not
     *  a midstep". Founder reads this on the review screen. */
    why: z.string().min(1).max(280),
    /** Where + when to fire it. Page, route, or event description, e.g.
     *  "on the /checkout/complete page after the Stripe redirect" or
     *  "on click of the 'request demo' submit button". */
    when: z.string().min(1).max(280),
    /** Paste-ready snippet. Usually a `window.mag.track(...)` line, but
     *  could also be a server-side webhook payload — see snippetKind. */
    snippet: z.string().min(1).max(2000),
    /** What flavour of code this is. Drives the copy button label +
     *  light syntax-highlight hints in the review surface. */
    snippetKind: z.enum(['html', 'js', 'server']),
});
/** The full "tracking pass" Mag delivers for an analytics_setup
 *  campaign. Shipped on the campaigns.draft jsonb column alongside
 *  (the existing) creative-ads CampaignDraft — the dashboard branches
 *  on campaign.kind to pick the right renderer. */
export const analyticsSetupDraftSchema = z.object({
    kind: z.literal('analytics_setup'),
    /** Mag's name for the pass, lowercase ("wizard completion pass"). */
    name: z.string().min(1).max(80),
    /** 2-3 sentence prose pitch in Mag's voice — opens the REVIEW screen.
     *  Explains why these events, why not others, what becomes possible
     *  once they're firing. */
    pitch: z.string().min(1).max(1000),
    /** 3-8 events, in install order. Mag picks fewer-but-meaningful
     *  rather than dumping a generic checklist. */
    events: z.array(analyticsSetupEventSchema).min(3).max(8),
    /** Pre-conditions the founder needs to get right before any of the
     *  snippets work (e.g. "the mag.js tag has to be on every page").
     *  Up to 5 short bullets. Optional. */
    prerequisites: z.array(z.string().min(1).max(280)).max(5).default([]),
    /** What Mag promises to do once events are firing — the loop-closer
     *  that justifies the work. "Once this is live i'll set up a daily
     *  funnel read and flag drop-offs > 30%." */
    postInstallCheck: z.string().min(1).max(400),
    /** The campaign's stated goal — what Mag is watching for. One short
     *  sentence in her voice, e.g. "drive landing→paid above 5%" or
     *  "cut wizard step-3 drop-off below 30%". Anchors every proactive
     *  insight: each scan asks "are we closer to this, further, or
     *  flat?". Optional for backward compatibility with pre-s125 drafts
     *  — when missing, the insight agent derives a working goal from
     *  the event set. */
    goal: z.string().min(1).max(200).optional(),
});
/** Mag's proactive read of a campaign's progress against its goal.
 *  One row per scan in the campaign_insights table. Renderable as a
 *  card on the launched-campaign page or (future) the home feed. */
export const campaignInsightSchema = z.object({
    id: z.string().uuid(),
    campaignId: z.string().uuid(),
    generatedAt: z.string(),
    /** Short title in Mag's voice, lowercase. Under 80 chars. */
    headline: z.string().min(1).max(120),
    /** 2-3 sentences citing the concrete numbers Mag observed and how
     *  they relate to the goal. */
    body: z.string().min(1).max(1200),
    /** One imperative line — the concrete change Mag suggests. */
    recommendation: z.string().min(1).max(400),
    costUsd: z.number().min(0),
    model: z.string(),
    /** Event-count tally Mag saw in this scan window. Map of event-name
     *  → integer count over the campaign's drafted event set. Used by
     *  the dashboard to render baseline-anchored "Y→Z" diff chips
     *  without trusting the agent's prose to remember exact numbers.
     *  Null on rows persisted before s127. */
    windowSnapshot: z.record(z.string(), z.number()).nullable(),
    /** Did Mag flag this read as worth surfacing to the home feed? Set
     *  from the agent on every scan (s149). The campaign page shows
     *  every insight regardless; this gate only controls the feed-card
     *  mirror on scheduled scans, so "you're on track" reads stay on
     *  the campaign and don't clutter the inbox. */
    material: z.boolean(),
    /** Per-insight conversation thread (s159.7). When the founder asks
     *  about THIS insight specifically — "why do you think that?",
     *  "what's the simplest next test?" — both her question and mag's
     *  answer land here. Reuses the magChatMessage shape so the same
     *  chat-bubble pattern renders both this thread and the home
     *  feed's mag_chat threads. toolCalls (s172d) carries each mag
     *  turn's analytics investigation when present. */
    thread: z
        .array(z.object({
        role: z.enum(['founder', 'mag']),
        body: z.string().min(1).max(4000),
        createdAt: z.string().datetime(),
        toolCalls: z
            .array(z.object({
            id: z.string(),
            name: z.string(),
            arguments: z.record(z.string(), z.any()),
            result: z.string(),
            isError: z.boolean(),
            durationMs: z.number().nonnegative(),
        }))
            .optional(),
    }))
        .default([]),
    /** Investigation transcript the scan agent made before writing
     *  this read (s172d). Empty array on rows born before s172d or on
     *  reads where mag answered from the header counts alone. */
    toolCalls: z
        .array(z.object({
        id: z.string(),
        name: z.string(),
        arguments: z.record(z.string(), z.any()),
        result: z.string(),
        isError: z.boolean(),
        durationMs: z.number().nonnegative(),
    }))
        .default([]),
    /** Anomaly hint, when this scan was triggered off-cycle by the
     *  watcher (s177). Null on regular cadence reads. The UI renders a
     *  "↘ anomaly: spike on wizard_started" eyebrow on the card when
     *  set. Same shape as the watcher's hint. */
    anomalyHint: campaignAnomalyHintSchema.nullable().optional(),
});
/** Type guard for the organic-meta content-plan draft shape. The
 *  intake agent classifies as `organic_meta` → drafting runs the
 *  meta-content-plan agent → draft column gets the
 *  MetaContentPlanOutput payload. Renderer + downstream code keys
 *  off this guard to dispatch correctly. */
export function isMetaContentPlanDraft(draft) {
    return metaContentPlanOutputSchema.safeParse(draft).success;
}
// ─── Free-form campaign plans (s181) ────────────────────────────────────
//
// Replaces the rigid "kind-determines-schema" model with one where mag
// decides what she's shipping. A plan is a list of deliverables, each
// of a typed kind from a known catalogue. Mag picks the count + the
// mix per campaign — no "exactly 6 hooks" rule.
//
// Why this exists: the old shape forced every campaign into one of
// three boxes (creative_ads / analytics_setup / conversion_path), and
// within each box the schema was tighter still ("exactly 6 hooks").
// A real agency doesn't operate that way — they hear the brief and
// decide the mix of deliverables. mag should too.
//
// Catalogue today (v1):
//   - paid_ad_hook — the thing that was "6 hooks" before. ports the
//     existing hook + bg shape into the deliverables union so the same
//     UI tile renders on both legacy + plan drafts.
//
// Catalogue tomorrow (later sessions add these):
//   - instagram_post, instagram_carousel, instagram_story
//   - analytics_event (replaces the standalone AnalyticsSetupDraft path)
//   - landing_change, email_copy, audience_segment, ...
//
// Adding a new kind is: extend the deliverable union here + add a tile
// component on the dashboard side + add (optional) a publish path on
// the api. No DB migration; the draft column is already jsonb.
/** One paid-ad hook deliverable — what "6 hooks" was, but now mag
 *  picks the count. Same hook + bg shape as the legacy
 *  campaignCreativeSchema so the existing HookCard tile renders both. */
export const paidAdHookDeliverableSchema = z.object({
    kind: z.literal('paid_ad_hook'),
    /** Stable id (s183) — required for publish + iterate actions that
     *  reference a specific deliverable inside the jsonb plan. Mag
     *  picks a short slug; legacy paid_ad_hook rows get synthetic ids
     *  generated by the dashboard adapter. */
    id: z.string().min(1).max(64),
    /** Short tagline copy. Lowercase, Instrument Serif italic in the UI. */
    hook: z.string().min(1).max(120),
    /** UI tile background — mag picks per variant so the batch reads as
     *  intentional, not random. */
    bg: z.enum(['spell', 'spell_soft', 'cream', 'ink']),
    /** One-line justification: why this angle, in mag's voice. The UI
     *  surfaces this on hover / drawer-open so the founder sees the
     *  thinking behind each hook. */
    why: z.string().min(1).max(280),
});
/** One Instagram post deliverable (s183). Mag drafts a caption +
 *  describes the image she'd ship; the founder reviews and either
 *  ships immediately (real Graph API call in s184; mock publish in
 *  s183) or asks her to revise. Real image generation lands in
 *  s185 — until then the `image_brief` is the founder's prompt to
 *  pair with whatever image-gen tool they're using. */
export const instagramPostDeliverableSchema = z.object({
    kind: z.literal('instagram_post'),
    /** Stable id (same role as on every other deliverable kind). */
    id: z.string().min(1).max(64),
    /** Mag's draft caption, lowercase mag-voice. Limit matches IG's
     *  ~2200-character caption ceiling so the founder doesn't draft
     *  something that'll be silently truncated. */
    caption: z.string().min(1).max(2200),
    /** Mag's prose description of what the image should show. v1
     *  ships this as text only — the founder can paste it into their
     *  image-gen tool of choice. s185 wires this through fal.ai /
     *  Playwright templates for auto-render. */
    image_brief: z.string().min(1).max(800),
    /** Publicly-accessible URL of the rendered image (s189). Meta's
     *  Content Publishing API needs a real URL — it pulls the image
     *  itself; no upload-by-bytes. Populated by the s190 image-gen
     *  flow (fal.ai / Playwright → R2). Optional on the schema so the
     *  founder can still review the draft + mock-publish without an
     *  image; real publish refuses when this is missing and falls
     *  back to mock. */
    image_url: z.string().url().max(2048).optional(),
    /** Mag's recommendation for when to post. Free-text — "tuesday
     *  morning", "right after you ship the wizard update", "wait
     *  until friday so we have data first". */
    post_when: z.string().min(1).max(160),
    /** One-line justification: why this post, in mag's voice. */
    why: z.string().min(1).max(280),
    /** Set when the post has been shipped. s183 sets this on mock
     *  publish; s184 sets it on real Graph API publish. Null/absent
     *  means "still in draft". */
    published_at: z.string().datetime().optional(),
    /** The platform-side post id once shipped. For s183 mock publishes
     *  this is `mock_ig_<random>`; s184 will store the real Graph
     *  media id. Lets future metrics-read tools find the post on
     *  Meta's side without re-posting. */
    published_post_id: z.string().min(1).max(120).optional(),
    /** How the post was shipped — 'mock' (s183 stub) or 'meta' (real
     *  Graph API, s184). The UI surfaces a mock-published badge so
     *  the founder isn't confused into thinking it's live on IG. */
    published_via: z.enum(['mock', 'meta']).optional(),
});
/** One analytics-event deliverable (s182) — replaces the per-event
 *  rows that used to live inside AnalyticsSetupDraft.events. Same
 *  shape as analyticsSetupEventSchema with a kind discriminator so
 *  the deliverables union can carry it. Mag picks the count + the
 *  exact events to instrument (same domain expertise as before;
 *  she's just no longer locked to 3-8). */
export const analyticsEventDeliverableSchema = z.object({
    kind: z.literal('analytics_event'),
    /** Stable id so the review UI can keyframe per-event animations
     *  and copy-buttons key off something predictable. */
    id: z.string().min(1).max(64),
    /** Event name as it should be fired ('purchase_completed',
     *  'wizard_step_completed'). Lowercase, snake_case — mag picks. */
    name: z.string().min(1).max(80),
    /** Mag's one-line justification — same field as paid_ad_hook.why. */
    why: z.string().min(1).max(280),
    /** Where + when to fire it. Page, route, or event description. */
    when: z.string().min(1).max(280),
    /** Paste-ready snippet — usually a `window.mag.track(...)` line. */
    snippet: z.string().min(1).max(2000),
    /** Code flavour for syntax-hint + copy-button labelling. */
    snippetKind: z.enum(['html', 'js', 'server']),
});
/** Meta paid-ad campaign deliverable (s193). Different from the
 *  organic instagram_post: this kind goes through Meta's Marketing
 *  API (Campaign → AdSet → AdCreative → Ad cascade) and actually
 *  spends money. Mag drafts the hook + image brief + audience +
 *  budget + optimization goal; the founder approves; the launch
 *  action ships the campaign to Meta as PAUSED (so the founder
 *  reviews on Meta's side before activating). */
export const metaPaidAdDeliverableSchema = z.object({
    kind: z.literal('meta_paid_ad'),
    id: z.string().min(1).max(64),
    /** Short tagline / primary text for the ad. Lowercase, mag voice.
     *  Becomes the message in the AdCreative's link_data. */
    hook: z.string().min(1).max(280),
    /** Prose description of the image (subject, vibe, palette,
     *  framing). Same shape as instagram_post.image_brief — same
     *  image-gen pipeline. */
    image_brief: z.string().min(1).max(800),
    /** Public CDN URL of the rendered image. Required for launch
     *  (Meta needs an accessible image url for the adimages upload).
     *  Populated by the image-gen flow before launch. */
    image_url: z.string().url().max(2048).optional(),
    /** Mag's prose description of who this ad targets. Founder-facing.
     *  v1 uses a default broad audience (geo + IG placements only);
     *  later sessions wire detailed targeting from this prose. */
    audience_brief: z.string().min(1).max(400),
    /** Daily budget in USD (whole dollars). Meta wants minor units
     *  (cents) at the API layer; we convert on launch. */
    daily_budget_usd: z.number().int().min(1).max(10_000),
    /** Meta's optimization goal — what the ad set optimizes delivery
     *  for. v1 catalogue is the four most common for SMB ads.
     *  - REACH: maximize unique people who see the ad (brand awareness)
     *  - LINK_CLICKS: maximize clicks (traffic)
     *  - POST_ENGAGEMENT: maximize likes/comments/shares (engagement)
     *  - CONVERSIONS: optimize toward a pixel event (needs pixel setup) */
    optimization_goal: z.enum(['REACH', 'LINK_CLICKS', 'POST_ENGAGEMENT', 'CONVERSIONS']),
    /** Optional destination URL when optimization_goal=LINK_CLICKS. If
     *  omitted on a LINK_CLICKS ad, mag falls back to the brand's
     *  marketing_url. */
    destination_url: z.string().url().max(2048).optional(),
    /** One-line justification: why this ad, in mag voice. */
    why: z.string().min(1).max(280),
    /** Set when shipped to Meta. v1 always launches as PAUSED so the
     *  founder approves on Meta's side before spend starts. */
    launched_at: z.string().datetime().optional(),
    /** The Meta-side campaign id (act_X format). Lets future insights
     *  reads find the campaign without re-querying. */
    launched_campaign_id: z.string().min(1).max(120).optional(),
    /** 'mock' or 'meta' — same distinction as instagram_post's
     *  published_via. UI surfaces a distinct badge for mock launches. */
    launched_via: z.enum(['mock', 'meta']).optional(),
    /** s194 — latest performance snapshot from Meta's Insights API.
     *  Refreshed on demand via POST /campaigns/:id/deliverables/:did/
     *  refresh-metrics. Stored on the deliverable so the dashboard
     *  doesn't need a separate fetch; persistence + historical
     *  rollups are a future session. */
    metrics: z
        .object({
        impressions: z.number().int().min(0),
        clicks: z.number().int().min(0),
        reach: z.number().int().min(0),
        spend_usd: z.number().min(0),
        ctr_percent: z.number().min(0),
        cpm_usd: z.number().min(0),
        /** Meta's current effective status on the ad — lets the UI
         *  warn the founder when an ad they launched is still PAUSED. */
        status: z.enum(['ACTIVE', 'PAUSED', 'DELETED', 'ARCHIVED', 'UNKNOWN']),
        /** When this snapshot was fetched. */
        fetched_at: z.string().datetime(),
        /** 'mock' or 'meta' — distinguishes synthesized numbers from
         *  the real api on the dashboard. */
        fetched_via: z.enum(['mock', 'meta']),
        /** cap3 — conversion fields mirrored from
         *  meta_paid_ad_metrics_history. Optional for back-compat
         *  with pre-cap3 inline snapshots stored in jsonb (the
         *  refresh path overwrites the whole metrics object so new
         *  reads always have these populated). */
        conversions: z.number().min(0).optional(),
        conversion_value_usd: z.number().min(0).optional(),
        conversion_rate: z.number().min(0).max(1).optional(),
        cost_per_conversion_usd: z.number().min(0).optional(),
    })
        .optional(),
    /** Phase A3b — autopilot auto-pause audit. Stamped when the
     *  paid-autopause scheduler decided this ad's CTR + spend
     *  pattern was flat enough to pause + actually issued the
     *  pause API call. Founder can clear via the unpause endpoint
     *  which also un-pauses on Meta's side. */
    auto_paused_at: z.string().datetime().optional(),
    /** Why mag paused — receipts in her voice. ≤280 chars,
     *  lowercase. ("ctr 0.4% over 4 days at $11.20 spend — well
     *  below your 2% baseline. paused; redraft to differentiate.") */
    auto_pause_reason: z.string().min(1).max(280).optional(),
    /** s215 phase 2a — founder-initiated pause audit. Stamped when
     *  a manager/editor pauses this ad from chat (or future dashboard
     *  surface). Kept distinct from auto_paused_at so the §0a.2
     *  reversal-within-24h mistake signal doesn't misfire on the
     *  founder's own pauses (resume only drops authority when the
     *  paused state had auto_paused_at set, not paused_at).
     *  ISO datetime. */
    paused_at: z.string().datetime().optional(),
    /** uuid of the user who paused. null on autopilot pauses. */
    paused_by_user_id: z.string().uuid().optional(),
    /** Optional reason the founder gave (mag voice). Free-form short
     *  string the founder typed in chat (e.g. "not converting on
     *  weekends, try fridays"). */
    pause_reason: z.string().min(1).max(280).optional(),
    /** Phase 1A — bench primitive. When true, the drafter produced
     *  this variant as a held alternative; the autolauncher skips it
     *  at launch time. The hook-rotation cycle activates it (flips
     *  the flag false → lets autolaunch ship it) when its sibling
     *  fatigues. Null/undefined on every legacy row — only the
     *  panel drafter sets this. */
    hold_for_rotation: z.boolean().optional(),
    /** When the hook-rotation cycle promoted this variant off the
     *  bench (cleared `hold_for_rotation`). Receipt for the audit
     *  trail — mag's followup card cites this to explain why a new
     *  ad just went live without a fresh proposal. */
    promoted_from_bench_at: z.string().datetime().optional(),
    /** Which sibling's fatigue triggered the promotion. The id of
     *  the auto-paused deliverable in the same campaign whose
     *  underperformance freed the rotation slot. Null on the live
     *  variant; set on promoted bench variants for traceability. */
    promoted_after_sibling_id: z.string().min(1).max(64).optional(),
});
/** Google Search ad deliverable (s205). The Google-Ads analog of
 *  meta_paid_ad: Mag drafts a full Search campaign proposal —
 *  campaign name + daily budget + ad group + RSA + keywords —
 *  the founder reviews the spec inline, and clicks launch to ship
 *  the 5-step Create chain (budget → campaign → ad group → ad →
 *  keywords) atomically via the Google Ads API.
 *
 *  Same lifecycle fields as meta_paid_ad: optional launched_at +
 *  launched_campaign_id once the create chain succeeds, plus
 *  launched_via to distinguish 'mock' from 'google'. */
export const googleSearchAdDeliverableSchema = z.object({
    kind: z.literal('google_search_ad'),
    id: z.string().min(1).max(64),
    /** Founder-facing campaign name. The Real client prepends "Magig — "
     *  in the customer's Google Ads UI so the row is obviously ours. */
    campaign_name: z.string().min(1).max(120),
    /** Daily budget in whole USD. Same cap as the meta side. */
    daily_budget_usd: z.number().int().min(1).max(10_000),
    /** Mag's prose description of the ad-group's targeting + voice. */
    audience_brief: z.string().min(1).max(400),
    /** Ad group name. */
    ad_group_name: z.string().min(1).max(120),
    /** RSA headlines — Google requires 3-15 short variants. */
    headlines: z
        .array(z.object({
        text: z.string().min(1).max(30),
        /** Optional pin (HEADLINE_1 / HEADLINE_2 / HEADLINE_3). Most
         *  founders should leave unpinned; mag uses pinning for the
         *  brand name when the founder's positioning depends on it. */
        pinnedField: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).optional(),
    }))
        .min(3)
        .max(15),
    /** RSA descriptions — Google requires 2-4 lines of 90 chars max. */
    descriptions: z
        .array(z.object({ text: z.string().min(1).max(90) }))
        .min(2)
        .max(4),
    /** Landing-page URL. Required because every RSA has a destination. */
    final_url: z.string().url().max(2048),
    /** Initial keyword set. Each keyword has a match type:
     *  EXACT (tightest), PHRASE (medium), BROAD (widest reach).
     *  v1 caps at 30 — anything more and the founder should be
     *  iterating on the ad group not adding terms here. */
    keywords: z
        .array(z.object({
        text: googleKeywordTextSchema,
        matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
    }))
        .min(1)
        .max(30),
    /** WHAT THIS CAMPAIGN MUST NOT MATCH — spec `preflight-the-plan` S4.
     *
     *  Nothing in this product could express an exclusion until this field, and
     *  fiscalo is what that costs. Its `e-factura dgi` [PHRASE] keyword matched
     *  `efactura dgi`, `dgi facturacion electronica`, `dgi factura` and — read
     *  live off the search-term report — `https www efactura dgi gub uy`. Those
     *  people were looking for the TAX AUTHORITY'S OWN PORTAL; one of them typed
     *  its URL into Google. $19.80 of a $40.47 campaign went to terms that
     *  clicked and converted nobody, and the only surface that noticed paused
     *  the campaign for "flat ctr" — true about the wrong cause.
     *
     *  CAMPAIGN-LEVEL, NOT AD-GROUP-LEVEL. A deliverable is one campaign, and an
     *  exclusion a founder states is about the campaign's intent, not about
     *  which bucket of keywords happens to be matching today. Scoping it per
     *  group would mean a term excluded from one group still buys clicks through
     *  another — the same money, one indirection further from the founder. This
     *  maps to campaign_criterion with negative=true, applied after the create
     *  chain.
     *
     *  BROAD here does NOT mean what it means on a positive keyword: a negative
     *  broad term excludes a query only when the query contains ALL of its
     *  words, in any order. `dgi portal` [BROAD] blocks "portal dgi uruguay" and
     *  leaves "efactura para dgi" alone. It is the right default precisely
     *  because it is narrower than founders expect, not wider.
     *
     *  OPTIONAL, for the same reason extra_creatives and extra_ad_groups are:
     *  `campaigns.draft` is jsonb holding deliverables already validated against
     *  the old shape, and requiring this would break live campaigns on READ.
     *  Absence means "nothing excluded" — which is what every campaign launched
     *  before this field did — and must never be rendered as "we looked and
     *  there was nothing to exclude".
     *
     *  Max 50. A founder who needs more needs a shared negative keyword list,
     *  which is a different Google resource and not this field. */
    negative_keywords: z
        .array(z.object({
        text: googleKeywordTextSchema,
        matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
    }))
        .max(50)
        .optional(),
    /** Additional RSAs for the SAME ad group — spec `two-rsas-per-ad-group`.
     *
     *  Google recommends at least 2 responsive search ads per ad group and
     *  publishes the lift: a second RSA is worth +6.6% conversions at a similar
     *  cost per conversion, a third +3.7%, and 3 enabled is the ceiling. A
     *  single-ad group cannot rotate, which is why mag's own takeover review
     *  flags one (`reviewThinAdGroups`) — and until this field existed she had
     *  no way to ship anything else (theory 81edf7e).
     *
     *  OPTIONAL, AND THAT IS A COMPROMISE, not an oversight. Making it required
     *  would invalidate every deliverable already stored in `campaigns.draft`
     *  jsonb on live campaigns. So the floor is enforced by prompt here and by
     *  type on the strategist side, where proposals are not re-validated from
     *  history — spec residue RES-2 records what that asymmetry costs.
     *
     *  Max 2, because 1 + 2 = google's ceiling of 3 enabled RSAs.
     *
     *  `final_url` and `keywords` deliberately do NOT repeat per creative: they
     *  are ad-group level, and duplicating them would invite them to diverge.
     *
     *  THE SECOND AD MUST BE A DIFFERENT ANGLE, not a reshuffle. One RSA already
     *  permutes its 3-15 headlines, so two ads drawn from one pool of copy is
     *  the same experiment twice and buys none of the 6.6%. No schema can hold
     *  that — it is the generator's job (RES-1). */
    extra_creatives: z
        .array(z.object({
        headlines: z
            .array(z.object({
            text: z.string().min(1).max(30),
            pinnedField: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).optional(),
        }))
            .min(3)
            .max(15),
        descriptions: z
            .array(z.object({ text: z.string().min(1).max(90) }))
            .min(2)
            .max(4),
    }))
        .max(2)
        .optional(),
    /** Additional AD GROUPS inside this ONE campaign — spec c441e4c2.
     *
     *  THE BUDGET IS WHY THIS EXISTS. Google sets the daily budget on the
     *  CAMPAIGN and the ad groups under it compete for that one pool. A
     *  deliverable is one campaign, so running two angles used to mean two
     *  deliverables, which meant two campaigns, which meant `daily_budget_usd`
     *  charged TWICE. Measured on a live draft: mag planned "one campaign, two
     *  ad groups" at $7/day and would have shipped $14/day — double what she
     *  told the founder — with both campaigns carrying the same name.
     *
     *  She was right and the model was wrong. Her own plan said it three times:
     *  "keeping both groups under a single campaign budget lets google shift
     *  daily spend toward whichever group is winning the auction that day,
     *  instead of hard-capping each separately." That is exactly how the
     *  platform behaves, and it is what this field makes expressible.
     *
     *  `daily_budget_usd` ABOVE IS NOT PER AD GROUP and does not repeat here.
     *  One deliverable, one campaign, one budget, several groups sharing it —
     *  if a second budget ever appears in this shape, the fix has been undone.
     *
     *  Each group carries its OWN keywords, which is the entire point of a
     *  split: two buckets of intent competing for one pool. `final_url` stays
     *  deliverable-level and every group inherits it (RES-3).
     *
     *  OPTIONAL, for the same reason extra_creatives is: `campaigns.draft` is
     *  jsonb holding deliverables already validated against the old shape, and
     *  requiring this would break live campaigns on READ. So nothing in the
     *  type system stops a generator emitting sibling deliverables again — the
     *  prompt and the same-name warning are what hold that (RES-1).
     *
     *  Max 3 is symmetry with extra_creatives, NOT google's real per-campaign
     *  ad-group ceiling, which was not looked up (RES-2). */
    extra_ad_groups: z
        .array(z.object({
        ad_group_name: z.string().min(1).max(120),
        headlines: z
            .array(z.object({
            text: z.string().min(1).max(30),
            pinnedField: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).optional(),
        }))
            .min(3)
            .max(15),
        descriptions: z
            .array(z.object({ text: z.string().min(1).max(90) }))
            .min(2)
            .max(4),
        keywords: z
            .array(z.object({
            text: googleKeywordTextSchema,
            matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
        }))
            .min(1)
            .max(30),
        /** A second RSA for THIS group. Google recommends at least 2 per ad
         *  group (spec 040c2838); the primary group's live in
         *  `extra_creatives` above. */
        extra_creatives: z
            .array(z.object({
            headlines: z
                .array(z.object({
                text: z.string().min(1).max(30),
                pinnedField: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).optional(),
            }))
                .min(3)
                .max(15),
            descriptions: z
                .array(z.object({ text: z.string().min(1).max(90) }))
                .min(2)
                .max(4),
        }))
            .max(2)
            .optional(),
    }))
        .max(3)
        .optional(),
    /** One-line justification: why this campaign, in mag voice. */
    why: z.string().min(1).max(280),
    /** Set when shipped to Google Ads. v1 launches as ENABLED per the
     *  design doc — the explicit in-app approval IS the launch gate.
     *  (Different from meta_paid_ad which ships PAUSED; google's
     *  approval surface here is heavier so we honor the click.) */
    launched_at: z.string().datetime().optional(),
    /** The Google-side campaign resource id (numeric tail of
     *  "customers/<n>/campaigns/<n>"). Lets future status changes +
     *  budget edits target the launched campaign directly. */
    launched_campaign_id: z.string().min(1).max(120).optional(),
    /** 'mock' (no dev token) or 'google' (real create chain). UI
     *  surfaces a distinct badge for mock launches. */
    launched_via: z.enum(['mock', 'google']).optional(),
    /** Phase A3b — autopilot auto-pause audit. Same shape + meaning as
     *  metaPaidAdDeliverableSchema.auto_paused_at. */
    auto_paused_at: z.string().datetime().optional(),
    /** Why mag paused — receipts in her voice. ≤280 chars, lowercase. */
    auto_pause_reason: z.string().min(1).max(280).optional(),
    /** s215 phase 2a — founder-initiated pause audit. Same shape +
     *  meaning as metaPaidAdDeliverableSchema. Distinct from
     *  auto_paused_at so the reversal-within-24h mistake signal stays
     *  scoped to autopilot pauses only. */
    paused_at: z.string().datetime().optional(),
    paused_by_user_id: z.string().uuid().optional(),
    pause_reason: z.string().min(1).max(280).optional(),
    /** Phase A6-finish — set when this deliverable was autodrafted by
     *  the meta-paid-creative-scheduler's google sibling from a
     *  strategy angle. A4 google autolaunch skips deliverables with
     *  this field set; the founder must explicitly approve via the
     *  approve-autodraft endpoint, which clears this field. Needed
     *  for google specifically (unlike meta, which has image_url as
     *  the implicit founder-action gate). */
    autopilot_drafted_at: z.string().datetime().optional(),
});
/** One deliverable in a campaign plan. Discriminated on `kind`. New
 *  kinds join the union as the catalogue grows. */
export const deliverableSchema = z.discriminatedUnion('kind', [
    paidAdHookDeliverableSchema,
    analyticsEventDeliverableSchema,
    instagramPostDeliverableSchema,
    metaPaidAdDeliverableSchema,
    googleSearchAdDeliverableSchema,
    // future: instagram_carousel, instagram_story, landing_change, email_copy
]);
/** Mag's free-form campaign plan (s181). Replaces the rigid kind-
 *  specific schemas. */
export const campaignPlanSchema = z.object({
    /** Discriminator so a parser can tell plans apart from legacy
     *  drafts without sniffing fields. */
    shape: z.literal('plan'),
    /** Mag's name for the campaign — lowercase, 1-8 words. Same
     *  convention as the legacy `name` field. */
    name: z.string().min(1).max(80),
    /** 2-3 sentence pitch in mag's voice. Opens the review screen and
     *  explains the deliverable mix — why this count, why these kinds. */
    pitch: z.string().min(1).max(1000),
    /** What success looks like, in mag's words. One sentence
     *  ("grow our weekly profile-visits past 800"). */
    goal: z.string().min(1).max(280),
    /** Mag's deliverables. Count is up to her. */
    deliverables: z.array(deliverableSchema).min(1).max(20),
    /** Audience description — optional. mag includes it when the
     *  campaign targets a specific cohort (paid runs); skips for
     *  campaigns where targeting isn't a knob (organic-only,
     *  analytics setup). */
    audience: campaignAudienceSchema.optional(),
    /** Budget block — optional. Set only when the campaign has paid
     *  spend (any paid_ad_hook or future paid_* deliverables). Mag
     *  may also include it for forecasting purposes. */
    budget: campaignBudgetSchema.optional(),
    /** What mag is anchoring success on (the metric / outcome she'll
     *  read against). One short answer ("profile visits/week",
     *  "wizard completion above 30%"). */
    optimizingFor: z.string().min(1).max(120),
    /** When the campaign goes live + when mag will check back. */
    timeline: z.string().min(1).max(160),
    /** Mag's self-critique: things she's genuinely uncertain about
     *  in this plan. The review screen surfaces these verbatim above
     *  the action row so the founder can read mag's caveats before
     *  approving. Empty array when mag is confident across the board
     *  (which she should be honest about — don't fake-flag). Cap
     *  raised from 400 → 800 so explanatory concerns (e.g. "google
     *  ads conversion tag is separate from magig analytics because
     *  …") can finish their thought instead of being trimmed mid-
     *  sentence by clipAtWord. */
    honestConcerns: z.array(z.string().min(1).max(800)).max(5).default([]),
    /** Pre-conditions the founder needs to get right before any
     *  deliverable can ship (s182 — lifted out of AnalyticsSetupDraft
     *  so any plan can carry them). E.g. "the mag.js tag must be on
     *  every page before the events fire", "the meta page admin role
     *  must be granted before posts can publish". Up to 5 short
     *  bullets, all optional. */
    prerequisites: z.array(z.string().min(1).max(280)).max(5).default([]),
    /** What mag promises to do once this campaign is live — the
     *  loop-closer that justifies the work. "Once events fire i'll
     *  set up a daily funnel read and flag drop-offs >30%."
     *  Optional. Lifted out of AnalyticsSetupDraft.postInstallCheck. */
    postInstallCheck: z.string().min(1).max(400).optional(),
    /** The one decision mag genuinely can't make alone. Same shape +
     *  same UI treatment as the legacy oneAsk. Optional — not every
     *  plan has a forced choice. */
    oneAsk: campaignOneAskSchema.optional(),
    /** Phase A5 — meta paid strategy proposal. Founder-triggered
     *  ("what should I test next?"). Stored inline on the plan so
     *  the campaign detail page can render the angles without a
     *  separate fetch. Only set on creative_ads campaigns; absent
     *  on other kinds. Optional + back-compat with pre-A5 plans. */
    paidStrategy: metaPaidStrategyOutputSchema.optional(),
    /** Phase A6 — google paid strategy proposal. Sibling of
     *  paidStrategy; same UX, RSA-shaped angles. Founder-triggered.
     *  Only set on creative_ads campaigns with google_search_ad
     *  deliverables. Optional + back-compat. */
    googlePaidStrategy: googlePaidStrategyOutputSchema.optional(),
    /** Capability gap "per-day rolling paid plans". 7-day rolling
     *  schedule of per-deliverable budget levels. Strategy
     *  (paidStrategy) is WHAT to test — schedule is WHEN at what
     *  budget. Founder-triggered draft; daily applier scheduler
     *  pushes today's row to the platforms via the existing budget
     *  mutation helpers. */
    paidSchedule: paidScheduleOutputSchema.optional(),
    /** Google counterpart. Same shape; separate so meta + google
     *  can refresh on independent cadences. */
    googlePaidSchedule: paidScheduleOutputSchema.optional(),
});
/** Type predicate: this draft is a v1 plan (s181 shape) vs the legacy
 *  CampaignDraft / AnalyticsSetupDraft. Lets callers branch on shape
 *  without parsing. */
export function isCampaignPlan(draft) {
    return draft.shape === 'plan';
}
/** Shape-agnostic accessor (s182). Returns the analytics-event names
 *  mag is tracking on this campaign — for legacy AnalyticsSetupDraft
 *  rows we pull from `events`; for plans we filter the deliverables
 *  union to `analytics_event` and read each `.name`. Empty array
 *  when the campaign has no analytics-event deliverables (e.g.
 *  creative_ads-only plans).
 *
 *  ORDER CONTRACT: the returned names are in FUNNEL ORDER — earliest
 *  touchpoint first, the conversion endpoint last. The analytics-setup
 *  agent is instructed to emit `events` in this order, and the on-site
 *  funnel compute (windowFunnel, order-sensitive) consumes it as the
 *  literal step sequence. Campaigns drafted before that instruction
 *  shipped may be out of order — their funnels under-count until
 *  re-drafted. */
export function extractAnalyticsEventNames(draft) {
    if (!draft)
        return [];
    if (isCampaignPlan(draft)) {
        return draft.deliverables
            .filter((d) => d.kind === 'analytics_event')
            .map((d) => d.name);
    }
    if (draft.kind === 'analytics_setup') {
        return draft.events.map((e) => e.name);
    }
    return [];
}
/** Shape-agnostic accessor (s182). Returns mag's stated goal on this
 *  campaign — `plan.goal` for new drafts, `draft.goal ?? null` for
 *  legacy analytics_setup, and `null` for legacy creative_ads (which
 *  didn't have a freestanding goal field). */
export function extractGoal(draft) {
    if (!draft)
        return null;
    if (isCampaignPlan(draft))
        return draft.goal;
    if (draft.kind === 'analytics_setup') {
        return draft.goal ?? null;
    }
    return null;
}
//# sourceMappingURL=campaign.js.map